import React, { useEffect, useRef, useState } from 'react';
import { Container, Card, CardBody, Form, Row, Col, FormGroup, Label, Input, Button, Alert } from 'reactstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaUser } from 'react-icons/fa';
import clientService from '../../services/clientService';
import ClientArticles from './ClientArticles';
import '../Fournisseur/SupplierForm.css';

const initialClient = {
    nom: '', prenom: '', cin: '', telephone1: '', telephone2: '', email: '',
    adresse: '', ville: 'Casablanca', typeClient: 'PARTICULIER', codePostal: '',
    plafond: 0, chiffreAffaires: 0, entreprise: '', ice: '', active: true,
    solde: 0, commentaire: '', chantiers: [],
};
const displayName = client => client.typeClient === 'ENTREPRISE'
    ? (client.entreprise || '') : [client.nom, client.prenom].filter(Boolean).join(' ');

export default function ClientForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const editId = params.id || location.state?.client?.id;
    const [client, setClient] = useState(initialClient);
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(Boolean(editId));
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [nameError, setNameError] = useState('');
    const [reload, setReload] = useState(0);
    const closeForm = () => location.pathname.startsWith('/sales-module')
        ? navigate('/sales-module/clients') : navigate(-1);

    useEffect(() => {
        if (!editId) {
            setClient(initialClient);
            setArticles([]);
            setLoading(false);
            setLoadError('');
            return undefined;
        }
        let active = true;
        setLoading(true);
        setLoadError('');
        Promise.all([clientService.getClientById(editId), clientService.articles(editId)])
            .then(([details, linked]) => {
                if (!active) return;
                setClient({ ...initialClient, ...details.data.body });
                setArticles(linked.data.body || []);
            })
            .catch(error => { if (active) setLoadError(error?.response?.data?.message || 'Impossible de charger le client et ses articles.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [editId, reload]);

    const changeField = event => {
        const { name, value } = event.target;
        setClient(current => ({ ...current, [name]: value }));
    };
    const handleSubmit = async event => {
        event.preventDefault();
        if (loading || savingRef.current || loadError) return;
        setSaveError('');
        const error = displayName(client).trim() ? '' : 'Indiquez le nom du client.';
        setNameError(error);
        if (error) return;
        if (articles.some(article => article.prixVenteTTC === '' || article.prixVenteTTC == null
            || !Number.isFinite(Number(article.prixVenteTTC)) || Number(article.prixVenteTTC) < 0)) {
            setSaveError('Saisissez un prix positif ou nul pour chaque article.');
            return;
        }
        savingRef.current = true;
        setSaving(true);
        const data = {
            ...client,
            chantiers: client.chantiers || [],
            articles: articles.map(article => ({ articleId: article.articleId, prixVenteTTC: Number(article.prixVenteTTC) })),
        };
        try {
            if (editId) await clientService.update(editId, data);
            else await clientService.addClient(data);
            closeForm();
        } catch (error) {
            setSaveError(error?.response?.data?.message || 'Impossible d’enregistrer le client.');
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const field = (name, label, type = 'text', placeholder = '') => <Col md={6} key={name}>
        <FormGroup><Label for={`client-${name}`}>{label}</Label>
            <Input id={`client-${name}`} name={name} type={type} value={client[name] || ''} placeholder={placeholder} onChange={changeField} />
        </FormGroup>
    </Col>;
    const locked = loading || saving || Boolean(loadError);

    return <Container fluid className="supplier-form-page py-3">
        <div className="supplier-form-shell">
            <Button type="button" color="link" className="supplier-back" disabled={saving} onClick={closeForm}><FaArrowLeft aria-hidden="true" /> Clients</Button>
            <header className="supplier-form-heading">
                <span className="supplier-form-icon"><FaUser aria-hidden="true" /></span>
                <div><h1>{editId ? 'Modifier le client' : 'Nouveau client'}</h1><p>Ses coordonnées, ses articles et ses prix de vente.</p></div>
            </header>
            {loading && <p role="status">Chargement…</p>}
            {loadError && <Alert color="danger">{loadError} <Button type="button" color="link" onClick={() => setReload(value => value + 1)}>Réessayer</Button></Alert>}
            {saveError && <Alert color="danger">{saveError}</Alert>}
            <Form onSubmit={handleSubmit} aria-busy={loading || saving}>
                <fieldset disabled={locked}>
                    <Card className="supplier-info-card"><CardBody>
                        <h2>Informations du client</h2>
                        <p className="supplier-form-hint">Seul le nom est obligatoire.</p>
                        <Row className="g-3">
                            <Col md={6}><FormGroup><Label for="client-name">Nom du client *</Label>
                                <Input id="client-name" value={displayName(client)} placeholder="Nom complet ou nom du commerce" invalid={Boolean(nameError)}
                                    onChange={event => {
                                        const value = event.target.value;
                                        setClient(current => current.typeClient === 'ENTREPRISE'
                                            ? { ...current, entreprise: value } : { ...current, nom: value, prenom: '' });
                                        setNameError('');
                                    }} />
                                {nameError && <div className="invalid-feedback">{nameError}</div>}
                            </FormGroup></Col>
                            {field('telephone1', 'Téléphone', 'tel', '06 12 34 56 78')}
                            {field('adresse', 'Adresse', 'text', 'Rue, quartier ou ville')}
                            {field('ice', 'ICE', 'text', 'Numéro ICE')}
                        </Row>
                        <details className="mt-2">
                            <summary>Informations complémentaires</summary>
                            <Row className="g-3 mt-1">
                                <Col md={6}><FormGroup><Label for="client-type">Type de client</Label>
                                    <Input id="client-type" type="select" value={client.typeClient} onChange={event => {
                                        const typeClient = event.target.value;
                                        setClient(current => {
                                            const name = displayName(current);
                                            return typeClient === 'ENTREPRISE'
                                                ? { ...current, typeClient, entreprise: current.entreprise || name }
                                                : { ...current, typeClient, nom: current.nom || name };
                                        });
                                        setNameError('');
                                    }}><option value="PARTICULIER">Particulier</option><option value="ENTREPRISE">Entreprise</option></Input>
                                </FormGroup></Col>
                                {field('cin', 'CIN')}
                                {field('telephone2', 'Téléphone secondaire', 'tel')}
                                {field('email', 'Email', 'email')}
                                {field('ville', 'Ville')}
                                {field('codePostal', 'Code postal')}
                                <Col xs={12}><FormGroup><Label for="client-commentaire">Commentaire</Label>
                                    <Input id="client-commentaire" name="commentaire" type="textarea" rows={2} value={client.commentaire || ''} onChange={changeField} />
                                </FormGroup></Col>
                            </Row>
                        </details>
                    </CardBody></Card>
                    <ClientArticles articles={articles} onChange={setArticles} disabled={locked} />
                    <footer className="supplier-form-footer mt-3">
                        <span>{articles.length} article{articles.length > 1 ? 's' : ''} associé{articles.length > 1 ? 's' : ''} à ce client</span>
                        <div><Button type="button" color="light" disabled={saving} onClick={closeForm}>Annuler</Button>
                            <Button type="submit" color="primary"><FaCheck aria-hidden="true" /> {saving ? 'Enregistrement…' : 'Enregistrer le client'}</Button></div>
                    </footer>
                </fieldset>
            </Form>
        </div>
    </Container>;
}
